import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { DateTime } from 'luxon';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { RestockingRuleSchedule, Weekday } from 'apis/restocking-rules/types';
import { usePluralization } from 'common/hooks';
import { Typography as T } from 'common/typography';

type Props = {
  data: RestockingRuleSchedule;
};

const Text = styled(T.Body3)`
  display: block;
`;

const SubText = styled(T.Small)`
  display: block;
  color: ${cbSemanticCssVars.textSecondary};
`;

const formatTimeFromMinutes = (minutes: number) => DateTime.local().startOf('day').plus({ minutes }).toFormat('HH:mm');

const SHORT_FORMAT_WEEKDAYS_MAP: Record<Weekday, string> = {
  MONDAY: DateTime.fromObject({ weekday: 1 }).toFormat('ccc'),
  TUESDAY: DateTime.fromObject({ weekday: 2 }).toFormat('ccc'),
  WEDNESDAY: DateTime.fromObject({ weekday: 3 }).toFormat('ccc'),
  THURSDAY: DateTime.fromObject({ weekday: 4 }).toFormat('ccc'),
  FRIDAY: DateTime.fromObject({ weekday: 5 }).toFormat('ccc'),
  SATURDAY: DateTime.fromObject({ weekday: 6 }).toFormat('ccc'),
  SUNDAY: DateTime.fromObject({ weekday: 7 }).toFormat('ccc'),
};

const FREQUENCY_TRANSLATIONS = [
  'menu.restockingRules.frequency.weekly',
  'menu.restockingRules.frequency.everyTwoWeeks',
  'menu.restockingRules.frequency.everyThreeWeeks',
  'menu.restockingRules.frequency.everyFourWeeks',
];

const RestockingRuleFrequency = ({ data }: Props) => {
  const { FormattedPluralMessage } = usePluralization();

  if (data.type === 'STATE_BASED') {
    return (
      <div>
        <Text>
          <FormattedMessage
            id="menu.restockingRules.frequency.everyday"
            values={{ time: formatTimeFromMinutes(data.byHour * 60 + data.byMinute) }}
          />
        </Text>
        <SubText>
          <FormattedPluralMessage id="menu.restockingRules.frequency.soldOutFor" count={data.dayCount} />
        </SubText>
      </div>
    );
  }

  if (data.frequency === 'DAILY') {
    return (
      <div>
        <Text>
          <FormattedMessage
            id="menu.restockingRules.frequency.everyday"
            values={{ time: formatTimeFromMinutes(data.byHour * 60 + data.byMinute) }}
          />
        </Text>
        <SubText>
          <FormattedMessage id="menu.restockingRules.frequency.daily" />
        </SubText>
      </div>
    );
  }

  return (
    <div>
      <Text>
        <FormattedMessage
          id="menu.restockingRules.frequency.byDay"
          values={{
            weekDays: data.byDay.map(day => SHORT_FORMAT_WEEKDAYS_MAP[day]).join(', '),
            time: formatTimeFromMinutes(data.byHour * 60 + data.byMinute),
          }}
        />
      </Text>
      <SubText>
        <FormattedMessage id={FREQUENCY_TRANSLATIONS[data.interval - 1]} />
      </SubText>
    </div>
  );
};

export default RestockingRuleFrequency;
